import {
  Component,
  inject,
  effect,
  signal,
  computed
} from '@angular/core';
import {
  faUser,
  faComments,
  faHeadset,
  faCogs,
  faPaperPlane,
  faQuestionCircle
} from '@fortawesome/free-solid-svg-icons';
import { ManagementUser } from '../../core/models/management-user.model';
import { SessionStorageService } from '../../core/services/session-storage.service';
import { LoginService } from '../../core/services/login.service';
import { SidebarService } from '../../core/services/sidebar.service';
import { ContactSupportService } from '../../core/services/contact-support.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { LoadingService } from '../../core/services/loading.service';
import introJs from 'intro.js';
import { contactIntroSteps } from './contact.intro-steps';
import { CATEGORY_ICONS } from 'src/app/shared/standards/icons';

@Component({
    selector: 'app-contact',
    templateUrl: './contact.component.html',
    styleUrls: ['./contact.component.css'],
    standalone: false
})
export class ContactComponent {
  private readonly loginService: LoginService = inject(LoginService);
  private readonly sessionStorageService: SessionStorageService = inject(SessionStorageService);
  private readonly sidebarService: SidebarService = inject(SidebarService);
  private readonly contactSupportService: ContactSupportService = inject(ContactSupportService);
  private readonly loadingService: LoadingService = inject(LoadingService);

  readonly supportIcon = CATEGORY_ICONS['support'];
  readonly userIcon = faUser;
  readonly topicIcon = faCogs;
  readonly messageIcon = faComments;
  readonly contactIcon = faHeadset;
  readonly sendIcon = faPaperPlane;
  readonly helpIcon = faQuestionCircle;

  readonly tourSteps = contactIntroSteps;

  readonly userLoggedIn = toSignal(this.loginService.loginStatus$, { initialValue: false });
  readonly userData = signal<ManagementUser | null>(null);
  readonly contactPreference = signal<string>('email');
  readonly fullName = signal<string>('');
  readonly topic = signal<string>('');
  readonly otherTopic = signal<string>('');
  readonly message = signal<string>('');
  readonly email = signal<string>('');
  readonly phone = signal<string>('');
  readonly errorMessage = signal<string | null>(null);
  readonly submitted = signal<boolean>(false);

  readonly isOtherSelected = computed(() => this.topic() === 'other');

  topicOptions = [
    { value: 'enrollment', name: 'Enrollment' },
    { value: 'subscriptions', name: 'Subscriptions' },
    { value: 'reports', name: 'Reports' },
    { value: 'student-acc-issue', name: 'Student Account Issue' },
    { value: 'adjust-diagnostic-schedule', name: 'Adjust Diagnostic Schedule' },
  ];

  constructor(
  ) {
    effect(() => {
      if (this.userLoggedIn()) {
        const data = this.sessionStorageService.userData() ;
        this.userData.set(data);

        if (data) {
          const firstName = data.firstName ?? '';
          const lastName = data.lastName ?? '';
          this.fullName.set(`${firstName} ${lastName}`);
          this.email.set(data.email ?? '');
        }
      }
    });
  }

  onSubmit() {
    let emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (this.contactPreference() === 'email' && !emailPattern.test(this.email())) {
      this.errorMessage.set('Please provide a valid email address.');
      return;
    }

    let phoneNumberPattern = /^(\+\d{1,2}\s?)?1?\-?\.?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
    if (this.contactPreference() === 'phone' && !phoneNumberPattern.test(this.phone())) {
      this.errorMessage.set('Please provide a valid phone number.');
      return;
    }


    this.errorMessage.set(null);
    this.loadingService.start();

    this.contactSupportService.sendEmail({
      senderName: this.fullName(),
      topic: this.isOtherSelected() ? this.otherTopic() : this.topic(),
      body: this.message(),
      contact: this.contactPreference() === 'email' ? this.email() : this.phone(),
    })
      .subscribe({
        next: (response: any) => {
          this.loadingService.finish();
          if(response.status === 'success'){
            this.submitted.set(true);
          }else if(response.status === 'fail'){
            this.errorMessage.set('Error submitting support request');
          }
        },
        error: () => {
          this.loadingService.finish();
          this.errorMessage.set('Error submitting support request');
        }
      });
  }

}
